import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import * as progressCloud from "../utils/progressCloud";
import { subscribeProgressSaved } from "../utils/progressEvents";
import { ACTIVITY_FEATURES, ActivityId, lastActiveByActivity, summarizeRecentActivity } from "../utils/activitySummary";

type LastActive = Partial<Record<ActivityId, Date>>;
type State = { identity?: string; status: "loading" | "ready" | "error"; lastActive: LastActive };

export function useRecentActivity(now?: Date) {
    const { status: authStatus, user } = useAuth();
    const identity = authStatus === "authenticated" ? user?.identityId : undefined;
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<State>({ status: "loading", lastActive: {} });

    useEffect(() => subscribeProgressSaved((change) => {
        if (change.userId === identity && ACTIVITY_FEATURES.includes(change.feature)) {
            setAttempt((value) => value + 1);
        }
    }), [identity]);

    useEffect(() => {
        if (!identity) return undefined;
        let cancelled = false;
        setState((current) => (current.identity === identity
            ? current
            : { identity, status: "loading", lastActive: {} }));
        const request = typeof progressCloud.getCloudProgressBatch === "function"
            ? progressCloud.getCloudProgressBatch(identity, ACTIVITY_FEATURES)
            : Promise.reject(new Error("Batch progress reads are unavailable."));
        request
            .then((rows) => {
                if (!cancelled) setState({ identity, status: "ready", lastActive: lastActiveByActivity(rows) });
            })
            .catch(() => {
                if (cancelled) return;
                setState((current) => (current.identity === identity && current.status === "ready"
                    ? current
                    : { identity, status: "error", lastActive: {} }));
            });
        return () => {
            cancelled = true;
        };
    }, [identity, attempt]);

    const visible = Boolean(identity) && state.identity === identity;
    const status = !identity ? "signed-out" : visible ? state.status : "loading";
    const lastActive = visible ? state.lastActive : null;
    const nowTime = now?.getTime();
    const summary = useMemo(() => {
        if (!lastActive) return null;
        try {
            return summarizeRecentActivity(lastActive, nowTime === undefined ? new Date() : new Date(nowTime));
        } catch {
            return null;
        }
    }, [lastActive, nowTime]);

    return { status, summary };
}
